import { refreshAll } from '$app/navigation';
import { handleApiError } from '#lib/util/error-handling/handle-api-error.ts';
import { pbCoreApiService } from './pb-core-api.service.svelte';
import { COL_USERS } from '#src/constants/collections.ts';

/** Extension of Pocketbase Service. Used on the server and on the client */
export class AuthService {
	async login({ username, password }: { username: string; password: string }): Promise<void> {
		const res = await fetch('/api/sign-in', {
			method: 'POST',
			body: JSON.stringify({ username: username.toLowerCase(), password })
		});
		await handleApiError(res);
		await refreshAll();
	}

	async loginWithOAuth2(provider: string, instance?: string): Promise<void> {
		// A new account gets its instance at creation, so the backend's org
		// auto-join looks in the right instance
		const authData = await pbCoreApiService.collection(COL_USERS).authWithOAuth2({
			provider,
			...(instance && { createData: { instance } })
		});

		if (!authData.record) {
			throw new Error('OAuth2 authentication failed');
		}

		// Send the auth token to our custom backend to set the auth cookie
		const res = await fetch('/api/oauth-callback', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify({
				token: authData.token,
				record: authData.record
			})
		});

		await handleApiError(res);

		// Clear PocketBase's localStorage auth - we use cookie auth exclusively
		pbCoreApiService.authStore.clear();

		await refreshAll();
	}

	async signup({
		email,
		firstName,
		lastName,
		password,
		instance
	}: {
		email: string;
		firstName: string;
		lastName: string;
		password: string;
		instance?: string;
	}): Promise<void> {
		const res = await fetch('/api/signup', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify({ email: email.toLowerCase(), firstName, lastName, password, instance })
		});
		await handleApiError(res);
	}

	async logout() {
		const res = await fetch('/api/logout', {
			method: 'POST',
			credentials: 'include' // Ensure cookies are sent with the request
		});
		await handleApiError(res);
		await refreshAll();
	}
}

export const authService = new AuthService();
